/**
 * Created by katarinababic on 23.6.25.
 */
import { ExploreEntry } from '../Model/Explore/ExploreEntry';
import { Recipe } from '../Model/Diet/Recipe/Recipe';
import { FitnessProgramTemplate } from '../Model/Coach/Program/Template/FitnessProgramTemplate';
import { useTrans } from '../Store/System/LocalizationStore';
import { MeditationEntry } from '../Model/Explore/MeditationEntry';
import { ActivityWorkoutEntry } from '../Model/Explore/ActivityWorkoutEntry';
import { WorkoutTemplateEntry } from '../Model/Explore/WorkoutTemplateEntry';
import { BreathingEntry } from '../Model/Explore/BreathingEntry';
import { WebinarEntry } from '../Model/Explore/WebinarEntry';
import dayjs from 'dayjs';
import { StringUtils } from './StringUtils';

/**
 * Custom hook to compute secondary text for an explore entry.
 */
export function useSecondaryText(entry?: ExploreEntry | Recipe | FitnessProgramTemplate) {
  const { t } = useTrans();

  if (!entry) {
    return undefined;
  }

  // Type guard: ExploreEntry has 'type' and 'contentType' properties
  const isExploreEntry = (e: ExploreEntry | Recipe | FitnessProgramTemplate): e is ExploreEntry =>
    'contentType' in e && 'type' in e;

  // Type guard: Recipe has 'nutrition' property
  const isRecipe = (e: ExploreEntry | Recipe | FitnessProgramTemplate): e is Recipe =>
    'nutrition' in e;

  if (isRecipe(entry)) {
    return `${Math.floor(entry.nutrition?.nutrients.calories?.amount || 0)} kcal`;
  } else if (isExploreEntry(entry)) {
    const hasDuration =
      entry.type === 'meditation' || entry.type === 'activityWorkout' || entry.type === 'workoutTemplate';

    if (hasDuration) {
      const entryWithDuration = entry as MeditationEntry | ActivityWorkoutEntry | WorkoutTemplateEntry;
      return t('globals.timeMin', { value: entryWithDuration.durationInMinutes });
    } else if (entry.type === 'breathing') {
      const breathingEntry = entry as BreathingEntry;
      const technique = breathingEntry.technique.map((tech) => tech.duration).join('-') || '4-4-4';
      return `${t('explore.breathing.technique')}: ${technique}`;
    } else if (entry.type === 'webinar') {
      const webinarEntry = entry as WebinarEntry;
      const timeMin = webinarEntry.dayjsDuration.asMinutes();
      const hosts = webinarEntry.hosts.map((h) => h.name).join(', ');
      const isUpcoming = webinarEntry.nextDate?.isAfter(dayjs());
      const upcomingWebinarSecondaryText = `${webinarEntry.nextDate?.format('MMM DD, LT')} • ${t('globals.timeMin', {
        value: timeMin,
      })} • ${hosts}`;
      const webinarSecondaryText = `${t('globals.timeMin', { value: timeMin })} • ${hosts}`;
      return isUpcoming
        ? StringUtils.shortenString(upcomingWebinarSecondaryText, 50)
        : StringUtils.shortenString(webinarSecondaryText, 50);
    }
  }

  return undefined;
}
